import { createContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "react-toastify";
import io from "socket.io-client";
import posthog from "posthog-js";
import { useDispatch, useSelector } from "react-redux";

import {
  API_URL,
  SOCKET_RETRY_ATTEMPTS,
  SOCKET_URL,
} from "../app/common/constants";
import { ChatListeners } from "../app/gig/chat/duck/listeners";
import { NotificationListeners } from "../app/notifications/duck/listener";
import Actions from "../app/login/duck/actions";
import ChatActions from "../app/gig/chat/duck/actions";
import {
  updateLeadStatus,
  updateLeadTranscript,
  addTranscriptMessage,
} from "../app/ai-outbound-calls/duck/actions";
import axios from "../app/common/axios"; // Use configured axios instance with interceptors

const WebSocketContext = createContext(null);
export { WebSocketContext };

const WebSocket = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retries, setRetries] = useState(0);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [reconnectionTrigger, setReconnectionTrigger] = useState(0);
  const userName = useSelector((state) => state.user?.profile?._id);
  const isAuthenticated = useSelector((state) => state.user?.isAuthenticated);

  const userRole = useSelector((state) => state?.user?.profile?.role);
  const isAdminOrModerator = userRole === "ADMIN" || userRole === "MODERATOR";

  const dispatch = useDispatch();
  const disconnectBannerId = useRef(null);
  const disconnectBannerTimeout = useRef(null);
  const socketRef = useRef(null);
  // True only while replaceSocket is deliberately tearing the socket down, so
  // the "disconnect" listener can tell an intentional swap from a real drop.
  const isReplacingSocket = useRef(false);
  // Pending deferred reconnects, so they can be cancelled on teardown.
  const reconnectTimers = useRef([]);
  const previousAccessToken = useRef(null);
  // The access token the live socket last handshook with. A `loginRejected`
  // compares against it to tell "renewal already happened, rebuild now" from
  // "renewal still pending, wait for the token to rotate".
  const socketToken = useRef(null);
  // Mirror of `retries` that handlers can read and bump synchronously, so the
  // rejection path does not have to run side effects inside a state updater.
  const retriesRef = useRef(0);
  const maxRetries = 3;
  // The reconnect banner has no natural end: it only clears on a `connect`
  // event. Give it a bounded lifetime so it turns into a real failed state
  // instead of spinning forever.
  const DISCONNECT_BANNER_TIMEOUT = 15000;
  const showDisconnectToast = () => {
    // Always dismiss any existing toast first to prevent duplicates
    if (disconnectBannerId.current) {
      toast.dismiss(disconnectBannerId.current);
    }
    if (disconnectBannerTimeout.current) {
      clearTimeout(disconnectBannerTimeout.current);
    }
    // Create a new toast and store its ID
    disconnectBannerId.current = toast.loading(
      "Not connected. Trying to reconnect now ...",
      {
        position: "bottom-right",
        toastId: "socket-disconnect-toast", // Using a constant ID prevents duplicates
      }
    );
    disconnectBannerTimeout.current = setTimeout(() => {
      if (disconnectBannerId.current) {
        toast.update(disconnectBannerId.current, {
          render: "Still offline. Check your connection and refresh.",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      }
    }, DISCONNECT_BANNER_TIMEOUT);
  };
  const dismissDisconnectToast = () => {
    if (disconnectBannerTimeout.current) {
      clearTimeout(disconnectBannerTimeout.current);
      disconnectBannerTimeout.current = null;
    }
    // Only dismiss our specific toast by ID
    if (disconnectBannerId.current) {
      toast.dismiss(disconnectBannerId.current);
      disconnectBannerId.current = null;
    }
  };

  // Cleanup any toasts when component unmounts
  useEffect(() => {
    return () => {
      if (disconnectBannerTimeout.current) {
        clearTimeout(disconnectBannerTimeout.current);
        disconnectBannerTimeout.current = null;
      }
      if (disconnectBannerId.current) {
        toast.dismiss(disconnectBannerId.current);
        disconnectBannerId.current = null;
      }
    };
  }, []);

  // Keep a ref to the live socket so replacement helpers can tear down the
  // current connection without depending on render-time closures.
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  // Tear down the current socket before creating a fresh one. With
  // `forceNew: true` an undisconnected socket keeps its own reconnect loop
  // and becomes an orphan.
  const replaceSocket = useCallback(() => {
    if (socketRef.current) {
      // socket.io-client v2 runs onclose synchronously inside disconnect(),
      // while our "disconnect" listener is still attached. Without this flag
      // the teardown raises the very "Not connected" banner this PR exists to
      // stop showing, and files a disconnect event that looks like a fault.
      isReplacingSocket.current = true;
      try {
        socketRef.current.disconnect();
      } finally {
        // Must clear even if disconnect throws, or a stuck flag would
        // suppress the reconnect banner for the rest of the session.
        isReplacingSocket.current = false;
      }
    }
    // Belt and braces, in case a transport closes the socket asynchronously
    // and the listener runs after the flag is cleared.
    dismissDisconnectToast();
    setSocket(null);
    setReconnectionTrigger((prev) => prev + 1);
  }, []);

  // Create stable handlers with useCallback to prevent stale closures
  const handleLoginConfirmed = useCallback(
    (_username) => {
      if (socket && userName) {
        dispatch(ChatListeners.loginConfirmed(socket, userName));
      }
    },
    [dispatch, socket, userName]
  );

  // The server rejected this socket's handshake and force-closed it. With
  // socket.io-client v2 an "io server disconnect" is final: the manager sets
  // skipReconnect, so nothing will revive this socket. Something here has to
  // build a new one with a token the server accepts.
  //
  // On a next-day visit that is the normal path: the access token (24h) has
  // expired, the socket handshakes with it before the axios interceptor has
  // finished renewing, and the server rejects it. Recovery must not hinge on
  // any one request succeeding — the presence nudge below shares its URL with
  // the presence heartbeat, and the interceptor's per-URL retry guard can
  // short-circuit it while that renewal is still in flight. The token
  // rotation itself is the reliable signal: the poller in the effect below
  // rebuilds a dead socket whenever the token changes.
  const handleLoginRejected = useCallback(
    (_username) => {
      console.info("WebSocket login rejected - attempting token renewal");

      if (retriesRef.current >= maxRetries) {
        console.error("WebSocket auth failed after", maxRetries, "retries");
        toast.error(
          "We are facing issue during authorization for real-time updates. Please try re-login to Nbyula. If the issue continues, reach out to support@nbyula.com",
          { autoClose: false }
        );
        dispatch(Actions.logout());
        window.location.reload();
        return;
      }

      // Check if user is still authenticated before making API call
      if (!isAuthenticated || !userName) {
        console.info("User no longer authenticated, skipping renewal");
        return;
      }

      retriesRef.current += 1;
      setRetries(retriesRef.current);

      // Renewal already landed before the rejection reached us (or a poll
      // tick staged the new token on a socket that was still handshaking).
      // Nothing to wait for: rebuild with the token we already have.
      const rejectedToken = socketToken.current;
      const latestToken = localStorage.getItem("accessToken");
      if (latestToken && latestToken !== rejectedToken) {
        console.info("Access token already rotated, rebuilding socket");
        replaceSocket();
        return;
      }

      // Nudge the axios interceptor into renewing by making an authenticated
      // request. If it gets through, the interceptor has either rotated the
      // token (the poller will rebuild — or we do it here to save a tick) or
      // confirmed the current one is fine for REST, in which case the socket
      // is rejecting a token the API accepts and a plain reconnect is the
      // only thing left to try before giving up at maxRetries.
      axios
        .post(`${API_URL}/v1/presence`, {
          entity_id: userName,
        })
        .then((_res) => {
          console.info("Presence request succeeded after socket rejection");
          replaceSocket();
        })
        .catch((err) => {
          // Expected while renewal is in flight: the interceptor rejects a
          // second request to a URL it is already retrying. The token poller
          // takes over once the renewed token lands in localStorage, and the
          // interceptor handles logout + reload if renewal itself fails.
          console.info(
            "Presence nudge did not complete; waiting for token rotation",
            err?.message || err
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, isAuthenticated, userName, maxRetries, replaceSocket]
  );

  // Track access token changes to detect renewal.
  useEffect(() => {
    const checkTokenChange = () => {
      const currentToken = localStorage.getItem("accessToken");
      if (currentToken && currentToken !== previousAccessToken.current) {
        previousAccessToken.current = currentToken;

        if (!socket || !isAuthenticated) {
          return;
        }

        // A dead socket cannot pick a new token up by itself. After the
        // server rejected the handshake (expired token on a next-day visit)
        // socket.io-client v2 has already given up: "io server disconnect"
        // sets skipReconnect and no reconnect loop is running. Staging the
        // token on it would be a no-op forever. Rebuild instead — this is
        // what makes the "Not connected" banner resolve.
        //
        // A socket that is mid-reconnect on its own (a transport drop) is
        // left alone: its loop reads opts.query on the next attempt, and
        // tearing it down would dismiss the banner while the network is
        // genuinely gone.
        const isDead = !socket.connected && !socket.io?.reconnecting;
        if (isDead) {
          console.info("Access token updated on a dead socket, rebuilding");
          replaceSocket();
          return;
        }

        // A routine 401 renewal rotates the token. Hand the fresh token to the
        // live socket so any future reconnect authenticates with it, but keep
        // the healthy connection open. Dropping it here fired a spurious
        // "reconnecting" banner on every renewal mid-session.
        // `socketToken` is deliberately left as the handshake token: if this
        // socket turns out to be mid-handshake and gets rejected, the
        // rejection handler sees the token has moved on and rebuilds at once.
        if (socket.io?.opts) {
          console.info("Access token updated, refreshing socket auth token");
          socket.io.opts.query = `userName=${userName}&token=${currentToken}`;
        }
      }
    };

    // Check immediately
    checkTokenChange();

    // Set up interval to check for token changes
    const intervalId = setInterval(checkTokenChange, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, [socket, isAuthenticated, userName, replaceSocket]);

  useEffect(() => {
    if (socket) {
      const handleTyping = (data) => dispatch(ChatListeners.onTyping(data));
      socket.on("loginConfirmed", handleLoginConfirmed);
      socket.on("loginRejected", handleLoginRejected);

      socket.on("roomMessage", (roomId, message) => {
        dispatch(ChatListeners.roomMessage(socket, roomId, message));
      });

      // updateRoomMessages
      socket.on("updateRoomMessages", (data) => {
        const { roomName, message } = data;
        dispatch(
          ChatListeners.roomMessage(socket, roomName, {
            ...message.content,
            messageId: message._id,
            topicId: message.topicId,
            rootCreatedAt: message.createdAt,
          })
        );
      });

      // conversationSync - refetch messages when backend adds a message
      socket.on("conversationSync", (data) => {
        dispatch(ChatListeners.conversationSync(data));
      });

      // Typing lives here, app-wide, rather than only on the chat page: an
      // indicator that arrives (or ends) while the user is on another route
      // must still reach the store, or it is stale when they come back.
      socket.on("typing", handleTyping);

      socket.on("roomJoinedEcho", (roomId) => {
        dispatch(ChatListeners.roomJoinedEcho(socket, roomId));
      });

      socket.on("directMessage", (message) => {
        dispatch(ChatListeners.directMessage(socket, message));
      });

      socket.on("systemMessage", (message) => {
        if (message?.type === "huddleWidget" && isAdminOrModerator) {
          dispatch(NotificationListeners.huddleWidgetNotification(message));
          return;
        }

        if (message?.type === "kickUserNotification") {
          dispatch(NotificationListeners.kickUserNotification(message));
          return;
        }

        // Handle call monitor events
        if (message?.type?.startsWith("call-monitor:")) {
          // Emit custom event for call monitoring components
          window.dispatchEvent(
            new CustomEvent("call-monitor-event", { detail: message })
          );
          return;
        }

        // Handle AI Outbound Calls status updates
        if (message?.type === "outbound-call:status-updated") {
          dispatch(updateLeadStatus(message));

          // Show toast notification
          const statusMessages = {
            calling: `Call started to lead`,
            completed: `Call completed`,
          };
          if (statusMessages[message.status]) {
            toast.info(statusMessages[message.status]);
          }
          return;
        }

        // Handle AI Outbound Calls transcript received
        if (message?.type === "outbound-call:transcript-received") {
          dispatch(updateLeadTranscript(message));

          // Show toast notification
          toast.success(
            `Call transcript received (${message.durationMinutes || 0} mins)`
          );
          return;
        }

        // Handle AI Outbound Calls real-time conversation messages
        if (message?.type === "outbound-call:conversation-message") {
          // Add each conversation message to the transcript
          if (message.message) {
            dispatch(addTranscriptMessage(message));
          }
          return;
        }

        dispatch(ChatListeners.systemMessage(socket, message));
      });

      socket.on("roomUserJoined", (roomName, userName) => {
        dispatch(ChatListeners.roomUserJoined(socket, roomName, userName));
      });

      socket.on("disconnect", (reason) => {
        setIsLoading(false);
        dispatch(ChatActions.setUserOnline(false));
        // Capture every disconnect so the real-time flow is measurable.
        // `intentional` separates our own token-rotation swaps from real
        // drops, so the disconnect rate stays readable.
        posthog?.capture("chat_socket_disconnected", {
          reason,
          authenticated: isAuthenticated,
          intentional: isReplacingSocket.current,
        });
        // Only show the reconnection banner for a real drop. A deliberate
        // replacement reconnects immediately and needs no warning.
        if (isAuthenticated && !isReplacingSocket.current) {
          showDisconnectToast();
        }
      });

      socket.on("connect", () => {
        setIsLoading(false);
        dispatch(ChatActions.setUserOnline(true));
        // Reset retries and error state on successful connection
        retriesRef.current = 0;
        setRetries(0);
        setError(null);
        posthog?.capture("chat_socket_connected");
        dismissDisconnectToast();
      });

      socket.on("error", (error) => {
        console.error("Socket error occurred: ", error);
        setError(error);
        setIsLoading(false);

        // If auth error and we have retries left, trigger reconnection
        if (error.type === "UnauthorizedError" && retries < maxRetries) {
          console.info("Socket auth error, will trigger reconnection attempt");
          reconnectTimers.current.push(
            setTimeout(() => {
              replaceSocket();
            }, 1000)
          );
        }
      });

      socket.on("reconnect_failed", () => {
        console.error("Socket reconnection failed");
        setError("Socket reconnection failed");
        setIsLoading(false);

        // If we still have retries and user is authenticated, try manual reconnection
        if (
          retries < maxRetries &&
          isAuthenticated &&
          userName &&
          localStorage.getItem("accessToken")
        ) {
          console.info("Attempting manual reconnection after failure");
          reconnectTimers.current.push(
            setTimeout(() => {
              replaceSocket();
            }, 2000)
          );
        }
      });

      // Cleanup function for socket events
      return () => {
        // These fire after this effect is gone, and replaceSocket sets state.
        reconnectTimers.current.forEach(clearTimeout);
        reconnectTimers.current = [];
        socket.off("loginConfirmed", handleLoginConfirmed);
        socket.off("loginRejected", handleLoginRejected);
        socket.off("roomMessage");
        socket.off("updateRoomMessages");
        socket.off("conversationSync");
        socket.off("typing", handleTyping);
        socket.off("roomJoinedEcho");
        socket.off("directMessage");
        socket.off("systemMessage");
        socket.off("roomUserJoined");
        socket.off("disconnect");
        socket.off("connect");
        socket.off("error");
        socket.off("reconnect_failed");
      };
    } else {
      // Get token from localStorage
      const currentAccessToken = localStorage.getItem("accessToken");

      // Only create socket if authenticated with valid tokens and not already disconnecting
      if (
        userName &&
        currentAccessToken &&
        isAuthenticated &&
        !isDisconnecting
      ) {
        setIsLoading(true);
        setError(null);

        let query = `userName=${userName}&token=${currentAccessToken}`;
        let opts = {
          query,
          reconnect: true,
          transports: ["websocket"],
          path: import.meta.env.PROD ? "/discovery/socket/chat-service" : "",
          forceNew: true,
          reconnectionAttempts: SOCKET_RETRY_ATTEMPTS,
          timeout: 5000, // Increased timeout for better reliability
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          maxReconnectionAttempts: SOCKET_RETRY_ATTEMPTS,
        };
        socketToken.current = currentAccessToken;
        setSocket(io.connect(SOCKET_URL, opts));
      }
    }
  }, [
    socket,
    userName,
    retries,
    dispatch,
    isAuthenticated,
    isDisconnecting,
    reconnectionTrigger,
    handleLoginConfirmed,
    handleLoginRejected,
    isAdminOrModerator,
    replaceSocket,
  ]);

  // Handle logout cleanup
  useEffect(() => {
    if (!isAuthenticated && socket) {
      console.info("User logged out, cleaning up WebSocket");
      setIsDisconnecting(true);
      dismissDisconnectToast();

      // Clean disconnect
      socket.disconnect();
      setSocket(null);
      retriesRef.current = 0;
      setRetries(0);
      setError(null);
      setReconnectionTrigger(0); // Reset reconnection trigger
      previousAccessToken.current = null; // Clear previous token ref
      socketToken.current = null;

      // Reset disconnecting state after cleanup
      setTimeout(() => setIsDisconnecting(false), 100);
    }
  }, [isAuthenticated, socket]);

  return (
    <WebSocketContext.Provider
      value={{
        socket: socket,
        isSocketLoading: isLoading,
        isSocketError: error,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocket;
